import type { DeepStringLike, MergeLiteralObjects } from "./types";

export const portalStringsEn = {
  blocking_task: {
    pending_hoa_approval: {
      alertCtaText: "Resolve issue",
      alertHeader: "HOA approval needed to continue",
      drawerBtnText: "Confirm and resolve issue",
      drawerConfirmText: "By clicking this button you confirm your HOA approved this project.",
      drawerHeader: "Action required to proceed!",
      description:
        "We’re waiting for approval from your homeowner’s association (HOA) before we can move forward with your project. Please text or call us once you’ve received approval so we can proceed with the next steps.",
    },
    pre_existing_permits: {
      alertCtaText: "Resolve issue",
      alertHeader: "Local authority needed to continue",
      drawerBtnText: "Call Remi to resolve issue",
      drawerConfirmText: "",
      drawerHeader: "Action required to proceed!",
      description:
        "Your contractor is unable to pull new permits because there is an existing permit on your home. Reach out to your local municipality to resolve this issue.",
    },
    unpermitted_structure: {
      alertCtaText: "Resolve issue",
      alertHeader: "Local authority needed to continue",
      drawerBtnText: "Call Remi to resolve issue",
      drawerConfirmText: "",
      drawerHeader: "Action required to proceed!",
      description:
        "Your contractor is unable to pull new permits because there is an unpermitted structure on your home. Reach out to your local municipality to resolve this issue.",
    },
    homeowner_unresponsive: {
      alertCtaText: "Resolve issue",
      alertHeader: "Your response is needed to continue",
      drawerBtnText: "Call Remi to resolve issue",
      drawerConfirmText: "",
      drawerHeader: "Action required to proceed!",
      description:
        "We’ve been trying to reach you regarding your project, but haven’t had any luck so far. We’re unable to move forward with your project until we hear back from you.",
    },
    homeowner_unwilling: {
      alertCtaText: "Resolve issue",
      alertHeader: "Your approval is needed to continue",
      drawerBtnText: "Call Remi to resolve issue",
      drawerConfirmText: "",
      drawerHeader: "Action required to proceed!",
      description:
        "It seems you’re not quite ready to move forward with your project. Whenever you’re ready, please resolve this issue.",
    },
    sow_confirmation: {
      drawerHeader: "",
      drawerConfirmText: "",
      drawerBtnText: "",
      alertHeader: "Project confirmation needed to continue",
      description: "",
      alertCtaText: "Confirm details",
    },
    homeowner_sow_rejection: {
      drawerHeader: "We are working to resolve your issue",
      drawerConfirmText: "",
      drawerBtnText: "",
      alertHeader: "Your project is on hold. Please contact your sales representative.",
      description:
        "Your project requires help from your sales representative to proceed. Contact your sales representative to update the scope of work as necessary. Please expect a follow-up shortly.",
      alertCtaText: "",
    },
    homeowner_decision_delay: {
      drawerHeader: "Action required to proceed!",
      drawerConfirmText: "",
      drawerBtnText: "Call Remi to resolve issue",
      alertHeader: "Your response is needed to continue",
      description:
        "It seems you’re not quite ready to move forward with your project. Whenever you’re ready, please resolve this issue.",
      alertCtaText: "Resolve issue",
    },
  },
  hold_number: "Hold {0}",
  errors: {
    not_found: {
      title: "Error 404",
      description: "Oops... page not found. Check the URL or ",
      contact: "contact support.",
    },
    file_not_found: {
      title: "File not found",
      description: "The file you are looking for does not exist. Please check the link or ",
      contact: "contact support.",
    },
    sow_not_available: {
      title: "Quote unavailable",
      description: "The quote information is no longer available. Please ",
      contact: "contact support.",
    },
  },
  sow_confirmation: {
    confirmAndContinueBtn: "Confirm and continue",
    confirmAndCompleteBtn: "Confirm and complete",
    doNotConfirmBtn: "This doesn't look right",
    signToContinueBtn: "Sign to continue",
    rejectionReason: {
      header: "What doesn't look right?",
      description: "Tell us what needs to change so we can get it fixed without delay.",
      placeholder: "Describe what isn't right",
      submit: "Submit",
      cancel: "Go back",
    },
    siteVisitIntro: {
      heading: "Insurance Inspection Process",
      introduction:
        "Remi has received your information from {0} and will help coordinate the next steps for your claim. Here's what to expect:",
      closing: "Before we move forward, we just need you to confirm a couple quick details.",
      steps: {
        scheduleInspection: {
          title: "Schedule an Inspection",
          description: "A Remi inspector will be assigned to visit your home.",
        },
        inspectionDocumentation: {
          title: "Inspection & Documentation",
          description:
            "During the inspection, the inspector will gather photos and details about the condition of your property and any related issues.",
        },
        carrierReview: {
          title: "Review with Your Insurance Carrier",
          description:
            "Remi will send the inspection details to {0} so they can review the information and determine what repairs may be covered under your policy.",
        },
        nextSteps: {
          title: "Next Steps",
          description:
            "Once {0} completes their review, Remi will reach back out to discuss your options and help coordinate any approved repairs.",
        },
      },
    },
    contactVerification: {
      header: "Does your contact information look correct?",
      fields: {
        name: {
          label: "Name",
          placeholder: "Name",
          error: "Please provide a name",
        },
        phone: {
          label: "Phone number",
          placeholder: "Phone number",
          error: "Please provide a valid phone number",
        },
        email: {
          label: "Email",
          placeholder: "Email",
          error: "Please provide a valid email address",
        },
      },
    },
    addressVerification: {
      header: "Does your address look correct?",
      fields: {
        addressStreet: {
          label: "Street",
          placeholder: "Street",
          error: "Please provide a street address",
        },
        city: {
          label: "City",
          placeholder: "City",
          error: "Please provide a city",
        },
        stateCode: {
          label: "State",
          placeholder: "State",
          error: "Please provide a state",
        },
        zip: {
          label: "Zip code",
          placeholder: "Zip code",
          error: "Please provide a zip code",
        },
      },
    },
    roofVerification: {
      header: "Does this image properly represent your roof?",
      subheader: "If applicable, ensure separate structures are included.",
      alert: "Highlighted section must match roof work location",
    },
    projectDetailsConfirmation: {
      header: "Do your project details look correct?",
      subheader:
        "We want your project to go smoothly. Please ensure the project details are accurate before approving. Charges may apply if the below information is not correct.",
      roofItems: {
        removal: "Roof type being removed",
        existingRoof: "Existing roof type",
        installation: "Roof type being installed",
        color: "Roof color selection",
      },
      solarItems: {
        removal: "Panels to be removed",
        reinstallation: "Panels to be reinstalled",
        systemCount: "Number of systems",
      },
      acknowledgement:
        "I have confirmed the above information accurately represents my project and understand any changes may result in a change order.",
    },
    externalContractorInfo: {
      header: "Roofer contact information",
      subheader: "This helps us coordinate your project timing.",
      fields: {
        externalContractorName: {
          label: "Company name",
          placeholder: "Company",
        },
        externalContractorContactName: {
          label: "Contact name",
          placeholder: "First Last",
        },
        externalContractorPhone: {
          label: "Phone number",
          placeholder: "(123) 456-7890",
          error: "Please provide a valid phone number",
        },
        externalContractorRoofWorkStartDate: {
          label: "Estimated roof completion date",
          placeholder: "Date",
        },
      },
    },
    otherConfirmation: {
      fields: {
        hoaApprovalNeeded: {
          label: "Do you need HOA approval for your project?",
          placeholder: "Do you need HOA approval for your project?",
        },
        preferredContactMethod: {
          label: "Preferred contact method",
          placeholder: "Preferred contact method",
        },
        additionalInformation: {
          label: "Any additional information?",
          placeholder: "Outside dogs/cats, special instructions, etc.",
        },
      },
    },
    projectConfirmed: {
      btnText: "Continue",
      prePtoFlow: {
        header: "Project confirmed",
        subheader: "You have successfully confirmed your project details. Continue to view your project progress.",
      },
    },
    projectNotConfirmed: {
      header: "Remi will contact you shortly",
      subheader:
        "We have notified a Remi team member who will be in contact with you shortly to resolve any discrepancies.",
      btnText: "Contact Remi",
    },
    pendingIssueResolution: {
      header: "We are working to resolve your issue",
      subheader:
        "Your project requires help from your sales representative to proceed. Contact your sales representative to update the scope of work as necessary. Please expect a follow-up shortly.",
    },
  },
  whats_next: {
    header: "What's next",
  },
  header: "{0} trusted partner",
  type_of_work: {
    roof: "roof work",
    vent: "vent work",
    moss_removal: "moss removal",
    structural: "structural work",
    rfr: "site visit",
    removal: "system removal project",
    reinstall: "system reinstall",
    multiple_projects: "project",
  },
  pending_funding: {
    header: "Payment details pending",
    subheader: "We're waiting on your payment details. Check your email for details or contact Remi support.",
  },
  contact: {
    header: "Contact support",
    subheader: "If you have any questions or require assistance, please do not hesitate to contact our support team.",
    buttons: {
      text: "Text message",
      call: "Call support",
      email: "Email",
    },
  },
  project_complete: {
    header: "Project complete",
    survey_description: "Your project is complete. Please take a minute to tell us how we did.",
    survey_description_completed: "Thank you for your feedback!",
    take_survey_btn: "Take a survey",
  },
  project_setup: {
    header: "We’re setting up your project",
    description: "We’re finalizing a few things on our end. We will notify you soon about next steps.",
    take_survey_btn: "Take survey",
  },
  progress_tracker: {
    default_header: "Track your project progress",
    waiting_on_homeowner: "Waiting on homeowner action",
    dates: {
      inspection_completed: "Inspection completed",
      inspection_scheduled: "Inspection scheduled",
      work_completed: "Work completed",
      work_started: "Work started",
      scheduled_work_date: "Scheduled work date",
      estimated_start_week: "Estimated start week",
      pending: "Pending",
    },
    phases: {
      pending_intro_call: {
        title: "Project detail confirmation",
        description: "Before we begin work on scheduling your project, please confirm your project details.",
        confirm_btn: "Confirm your project details",
        view_project_details_btn: "View project details",
        view_project_details: {
          btn: "View project details",
          header: "View your project details",
          project_description: "Project description",
          image_note: "Work will be performed on the highlighted section of the roof",
        },
      },
      pending_permit: {
        title: "Permitting",
        description:
          "Your project requires permits from local municipalities. The necessary submissions will be made, and approvals will be obtained by your contractor. This step typically takes 2-3 weeks.",
      },
      pending_install_scheduling: {
        title: "Scheduling",
        description:
          "Your contractor will coordinate with you to schedule your {0}. We'll text you the confirmed date and send a reminder the day before the project begins.",
      },
      pending_install_complete: {
        title: "In production",
        description: "Your {0} is in progress. We'll notify you once the work is completed.",
      },
      pending_backfill: {
        title: "Tile reinstall",
        description: {
          solar_installation: {
            pre_pto:
              "We're awaiting confirmation from {0} regarding your solar installation. Once it is complete, your contractor will contact you to arrange the tile reinstall. To verify the status of the installation, please reach out to your Sunrun representative.",
            post_pto:
              "We're currently waiting for the completion of your solar installation. Once it is finished, your contractor will contact you to schedule your tile reinstallation.",
          },
          scheduling: "Your contractor will coordinate with you to schedule your tile reinstallation.",
          in_progress: "Your tile reinstallation is in progress.",
          completed: "Your tile reinstallation is complete.",
        },
      },
      pending_final_inspection: {
        title: "Final inspection",
        description:
          "A final inspection is required by your local municipality. We'll notify you once it is scheduled and completed.",
      },
    },
  },
  contractor: {
    submit_photos: {
      completedOn: "Completed on {0}",
      photosUploadedThankYou: "Thank you for uploading your project photos to Remi.",
      photosUploaded: "Photos uploaded!",
      header: "Submit photos",
      upload: "Upload",
      confirm: "Confirm",
      confirmProject: {
        title: "Is this the correct project?",
        description: "Confirm you are at the correct address, and uploading photos for the correct scope of work.",
      },
      address: "Address",
      project: "Project",
      uploadPhotosPrompt: "Upload photos and mark as done.",
      markAsDonePrompt: "Click “mark as done” to complete the task.",
      fullReRoof: "Full re-roof",
      saveAndContinue: "Save and continue",
      photo: "Photo",
      photos: "Photos",
      markAsDone: "Mark as done",
      addMore: "Add more",
      seePhoto: "See 1 photo",
      seePhotos: "See {0} photos",
      confirmCloseModal: {
        saveTitle: "Save changes",
        saveDescription: "Would you like to save the uploaded photos to the project?",
        uploadingTitle: "Photos are still uploading",
        uploadingDescription: "Your photos are still uploading, if you leave now your photos will be discarded.",
        discard: "Discard",
        save: "Save",
        stay: "Stay",
      },
      initialPhotos: {
        title: "Initial photos",
        description: "Take photos around the entire house and its surroundings.",
      },
      deckingCondition: {
        title: "Decking condition",
        description: "Ensure you capture images of unusable or ruined decking.",
      },
      underlaymentInstall: {
        title: "Underlayment",
        description:
          "Take photos of the underlayment installation. Focus on high risk areas such as; roof to wall connections, chimneys and valleys.",
      },
      completedWork: {
        title: "Completed work",
        roofWorkDescription:
          "Take photos of the completed roof. Also capture images of the entire home and its surroundings in a clean state.",
        structuralDescription:
          "Take photos of each sister joist, ensure the images adequately capture the nail pattern.",
        ventWorkDescription:
          "Take photos of the completed vents. Also capture images of the cleaned worksite and previous ladder location.",
        miscDescription: "Take photos of the completed work. Ensure you take images of a clean worksite.",
      },
      atticAccess: {
        title: "Attic access",
        description: "Capture images of the access to the attic.",
      },
      rafterCondition: {
        title: "Rafter condition",
        description: "Take images that show as many rafters possible.",
      },
      preparationWork: {
        title: "Preparation work",
        description: "Take photos of the removed vents and any new holes cut into the roof.",
      },
      baseFlashings: {
        title: "Base flashings",
        description: "Take photos of the new properly flashed vents.",
      },
      roofWithoutPanels: {
        title: "Roof without panels",
        description: "Take photos of the roof with the panels removed.",
      },
      storedPanels: {
        title: "Stored panels",
        removalDescription:
          "Take photos of the location the panels were stored. Show the panels and racking stored in an organized manner.",
        reinstallDescription: "Take photos capturing the condition of the stored panels.",
      },
      installPanels: {
        title: "Install panels",
        description:
          "Take photos of the installed panels. Ensure you show measurements of the panel setbacks, the racking and railing properly grounded and sealed flashings.",
      },
      systemValidation: {
        title: "System validation",
        description: "Take images of the inverter or screenshot the app to show the system is functioning.",
      },
    },
  },
  close: "Close",
} as const;

type PortalStringsEn = typeof portalStringsEn;

export const portalStringsEs = {
  ...portalStringsEn,
  progress_tracker: {
    ...portalStringsEn.progress_tracker,
    dates: {
      inspection_completed: "Inspección completada",
      inspection_scheduled: "Inspección programada",
      work_completed: "Trabajo completado",
      work_started: "Trabajo iniciado",
      scheduled_work_date: "Fecha de trabajo programada",
      estimated_start_week: "Semana de inicio estimada",
      pending: "Pendiente",
    },
  },
  sow_confirmation: {
    ...portalStringsEn.sow_confirmation,
    rejectionReason: {
      header: "¿Qué no se ve bien?",
      description: "Cuéntanos qué debe cambiar para que podamos corregirlo sin demora.",
      placeholder: "Describe qué no está bien",
      submit: "Enviar",
      cancel: "Regresar",
    },
    projectDetailsConfirmation: {
      ...portalStringsEn.sow_confirmation.projectDetailsConfirmation,
      roofItems: {
        ...portalStringsEn.sow_confirmation.projectDetailsConfirmation.roofItems,
        existingRoof: "Tipo de techo existente",
      },
    },
  },
  contractor: {
    submit_photos: {
      completedOn: "Completado el {0}",
      photosUploaded: "¡Fotos subidas!",
      photosUploadedThankYou: "Gracias por subir las fotos de tu proyecto a Remi.",
      header: "Subir imágenes",
      upload: "Subir",
      confirm: "Confirmar",
      confirmProject: {
        title: "Es este ele proyecto?",
        description:
          "Confirma que estás en la dirección correcta y que estás subiendo fotos para el alcance de trabajo correcto.",
      },
      address: "Dirección",
      project: "Proyecto",
      uploadPhotosPrompt: "Subir fotos y completar.",
      markAsDonePrompt: "Subir fotos y completar.",
      fullReRoof: "Reemplazo completo del techo entero",
      saveAndContinue: "Guardar y continuar",
      photo: "Foto",
      photos: "Fotos",
      markAsDone: "Completar",
      addMore: "Añadir más",
      seePhoto: "Ver 1 foto",
      seePhotos: "Ver {0} fotos",
      confirmCloseModal: {
        saveTitle: "Guardar cambios?",
        saveDescription: "Quisiera guardar los fotos antes que se sale de esta pagina?",
        uploadingTitle: "Las fotos se siguen subiendo",
        uploadingDescription: "Tus fotos todavía se están subiendo, si te vas ahora tus fotos serán descartadas.",
        discard: "Descartar",
        save: "Guardar",
        stay: "Quedarse",
      },
      initialPhotos: {
        title: "Fotos iniciales",
        description: "Toma fotos de la casa entera y sus alrededores.",
      },
      deckingCondition: {
        title: "Condicion del plywood",
        description: "Asegúrate de tomar fotos de las piezas dañadas o inservibles.",
      },
      underlaymentInstall: {
        title: "Papel",
        description:
          "Tomas fotos del instalación del revestimiento, enfocándote en las áreas de alto riesgo como las conexiones entre el techo y la pared, las chimeneas y los valles.",
      },
      completedWork: {
        title: "Instalación finalizada",
        roofWorkDescription:
          "Toma fotos del techo terminada y la casa entera y sus alrededores en un condición limpia.",
        structuralDescription:
          "Toma fotos de cada viga gemela, asegúrate que los imágenes muestran la forma de los clavos.",
        ventWorkDescription:
          "Toma fotos de cada ventilla nueva. Tambien tomas fotos de la casa entera y la ubicacion donde la escalera fuera puesta.",
        miscDescription: "Tomas photos del trabajo terminado. Asegúrese que tomas fotos de un sitio limpio.",
      },
      atticAccess: {
        title: "Acceso al ático",
        description: "Toma fotos del acceso al ático.",
      },
      rafterCondition: {
        title: "Condition de las vigas",
        description: "Toma fotos para que podemos ver todas las vigas.",
      },
      preparationWork: {
        title: "Trabajo de preparación",
        description: "Tomas fotos de las ventillas quitadas y los nuevos aguejeros que cortaron.",
      },
      baseFlashings: {
        title: "Flachin del base",
        description: "Tomas fotos de los ventillas con flachin adecuada.",
      },
      roofWithoutPanels: {
        title: "Techo sin panales",
        description: "Tomas fotos del techo con los paneles quitadas.",
      },
      storedPanels: {
        title: "Panales guardadas",
        removalDescription: "Tomas fotos de los ventillas con flachin adecuada.",
        reinstallDescription: "Asegúrate de tomar fotos de las piezas dañadas o inservibles.",
      },
      installPanels: {
        title: "Instalación del papel",
        description:
          "Tomas fotos del instalación del revestimiento, enfocándote en las áreas de alto riesgo como las conexiones entre el techo y la pared, las chimeneas y los valles.",
      },
      systemValidation: {
        title: "Validación del sistema",
        description: "Toma fotos del inversor o captura la pantalla de tu aplicación del solar.",
      },
    },
  },
  close: "Cerrar",
} as const satisfies DeepStringLike<PortalStringsEn>;

type PortalStringsEs = typeof portalStringsEs;

export type PortalStrings = MergeLiteralObjects<PortalStringsEn, PortalStringsEs>;
